import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tab,
  Tabs,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SimpleReactValidator from "simple-react-validator";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthProvider";
import { useTranslation } from "react-i18next";
import { useLang } from "../../context/LangContext";
import Fade from "@mui/material/Fade";
import "./style.scss";

const arabicMessages = {
  required: "الحقل مطلوب",
  email: "يرجى إدخال بريد إلكتروني صالح",
  phone: "يرجى إدخال رقم هاتف صالح",
  numeric: "يجب أن يكون الحقل رقمًا",
  password: "كلمة المرور يجب أن تكون 8 أحرف على الأقل",
  otp: "يرجى إدخال رمز التحقق",
};

const AuthModal = ({ open, handleClose }) => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const {
    logIn,
    loginWithPhone,
    verifyPhone,
    signUp,
    signUpWithPhone,
    verifyPhoneRegister,
  } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Modal state
  const [authTab, setAuthTab] = useState(0); // 0: Login, 1: Signup
  const [authMethod, setAuthMethod] = useState("phone"); // "email" or "phone"
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Validators
  const [validator] = useState(
    new SimpleReactValidator({
      className: "errorMessage",
      messages: lang === "ar" ? arabicMessages : undefined,
    })
  );

  // Form data
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
    phone: "",
    otp: "",
    remember: false,
  });

  const [signupData, setSignupData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    otp: "",
  });

  // Get the current form data based on the active tab
  const getCurrentFormData = () => {
    return authTab === 0 ? loginData : signupData;
  };

  // Change handlers
  const handleLoginChange = (e) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
    validator.showMessages();
  };

  const handleSignupChange = (e) => {
    setSignupData({ ...signupData, [e.target.name]: e.target.value });
    validator.showMessages();
  };

  const handleRememberChange = () => {
    setLoginData({ ...loginData, remember: !loginData.remember });
  };

  // Reset form
  const resetForm = () => {
    setLoginData({
      email: "",
      password: "",
      phone: "",
      otp: "",
      remember: false,
    });
    
    setSignupData({
      name: "",
      email: "",
      phone: "",
      password: "",
      confirm_password: "",
      otp: "",
    });
    
    setOtpSent(false);
    setAuthMethod("phone");
    setAuthTab(0);
  };

  // Handle modal close
  const handleModalClose = () => {
    resetForm();
    handleClose();
  };

  // Password validation for signup
  const validatePassword = (password) => /^(?=.*\d).{8,}$/.test(password);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setAuthTab(newValue);
    setOtpSent(false);
  };

  // Handle method change
  const handleMethodChange = (method) => {
    setAuthMethod(method);
    setOtpSent(false);
  };

  // Login form submission
  const handleLoginSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      if (authMethod === "email") {
        await logIn(loginData.email, loginData.password);
        toast.success(t(`LoginSuccessful`));
        handleModalClose();
      } else {
        const response = await loginWithPhone(loginData.phone);
        if (response.success) {
          toast.success(t(`OTPSent`));
          setOtpSent(true);
          return;
        }
      }
    } catch (error) {
      toast.error(t(`LoginFailed`));
    } finally {
      setLoading(false);
    }
  };

  // Signup form submission
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (authMethod === "email") {
      const { password, confirm_password } = signupData;

      if (!validatePassword(password)) {
        toast.error(t(`passwordValidation`));
        return;
      }

      if (password !== confirm_password) {
        toast.error(t(`passwordMatchError`));
        return;
      }

      if (validator.allValid()) {
        try {
          setLoading(true);
          await signUp(signupData);
          toast.success(t(`signupSuccess`));
          setAuthTab(0); // Switch to login tab after successful signup
        } catch (error) {
          toast.error(error.message);
        }
        setLoading(false);
      } else {
        validator.showMessages();
        toast.error(t(`fillRequiredFields`));
      }
    } else {
      try {
        setLoading(true);
        const response = await signUpWithPhone(
          signupData.phone,
          signupData.name,
          signupData.email
        );
        if (response.success) {
          toast.success(t(`OTPSent`));
          setOtpSent(true);
          return;
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    }
  };

  // Verify OTP for login
  const handleVerifyLoginOtp = async () => {
    if (!loginData.phone) {
      toast.error(t(`PhoneRequired`));
      return;
    }
    try {
      setLoading(true);
      await verifyPhone(loginData.phone, loginData.otp);
      toast.success(t(`LoginSuccessful`));
      handleModalClose();
    } catch (error) {
      toast.error(t(`LoginFailed`));
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP for signup
  const handleVerifySignupOtp = async () => {
    if (!signupData.phone) {
      toast.error(t(`PhoneRequired`));
      return;
    }
    try {
      setLoading(true);
      await verifyPhoneRegister(signupData.phone, signupData.otp);
      toast.success(t(`RegistrationSuccessful`));
      setAuthTab(0); // Switch to login tab after successful signup
      setOtpSent(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 500 }}
      fullWidth
      maxWidth="sm"
      className="auth-modal-dialog"
    >
      <DialogTitle>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          dir={lang === "ar" ? "rtl" : "ltr"}
        >
          <Tabs
            value={authTab}
            onChange={handleTabChange}
            aria-label="auth tabs"
          >
            <Tab label={t(`login`)} />
            <Tab label={t(`register`)} />
          </Tabs>
          <IconButton onClick={handleModalClose}>
            <CloseIcon />
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent className="auth-modal-content">
        <Grid className="authForm" dir={lang === "ar" ? "rtl" : "ltr"}>
          {/* Login Tab */}
          {authTab === 0 && (
            <>
              <p>{t(`loginDesc`)}</p>
              <div className="authToggle">
                <Button
                  onClick={() => handleMethodChange("email")}
                  disabled={authMethod === "email"}
                >
                  {t(`emailAndPassword`)}
                </Button>
                <Button
                  onClick={() => handleMethodChange("phone")}
                  disabled={authMethod === "phone"}
                >
                  {t(`phone`)}
                </Button>
              </div>

              <form onSubmit={handleLoginSubmit}>
                <Grid container spacing={3}>
                  {authMethod === "email" ? (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t(`email`)}
                          name="email"
                          value={loginData.email}
                          onChange={handleLoginChange}
                          variant="outlined"
                        />
                        {validator.message(
                          "email",
                          loginData.email,
                          "required|email"
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t(`password`)}
                          type="password"
                          name="password"
                          value={loginData.password}
                          onChange={handleLoginChange}
                          variant="outlined"
                        />
                        {validator.message(
                          "password",
                          loginData.password,
                          "required|password"
                        )}
                      </Grid>
                    </>
                  ) : !otpSent ? (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label={t(`phone`)}
                        name="phone"
                        value={loginData.phone}
                        onChange={handleLoginChange}
                        variant="outlined"
                        dir="ltr"
                      />
                      {validator.message(
                        "phone",
                        loginData.phone,
                        "required|phone"
                      )}
                    </Grid>
                  ) : (
                    authMethod === "phone" &&
                    otpSent && (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label={t(`otp`)}
                            name="otp"
                            value={loginData.otp}
                            onChange={handleLoginChange}
                            variant="outlined"
                            dir="ltr"
                          />
                          {validator.message(
                            "otp",
                            loginData.otp,
                            "required|numeric"
                          )}
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            fullWidth
                            onClick={handleVerifyLoginOtp}
                            disabled={loading}
                            variant="contained"
                            color="primary"
                          >
                            {t(`verifyOtp`)}
                          </Button>
                        </Grid>
                      </>
                    )
                  )}

                  <Grid item xs={12}>
                    {!otpSent && (
                      <>
                        <Grid className="formAction">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={loginData.remember}
                                onChange={handleRememberChange}
                              />
                            }
                            label={t(`rememberMe`)}
                          />
                          {/* <Link to="/forgot" onClick={handleModalClose}>
                            {t(`forgot`)}
                          </Link> */}
                        </Grid>
                        <Grid className="formFooter">
                          <Button
                            fullWidth
                            type="submit"
                            disabled={loading}
                            variant="contained"
                            color="primary"
                          >
                            {loading ? t(`loading`) : t(`login`)}
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Grid>
              </form>
            </>
          )}

          {/* Signup Tab */}
          {authTab === 1 && (
            <>
              <p>{t(`registerText`)}</p>
              <div className="authToggle">
                <Button
                  onClick={() => handleMethodChange("email")}
                  disabled={authMethod === "email"}
                >
                  {t(`emailAndPassword`)}
                </Button>
                <Button
                  onClick={() => handleMethodChange("phone")}
                  disabled={authMethod === "phone"}
                >
                  {t(`phone`)}
                </Button>
              </div>

              <form onSubmit={handleSignupSubmit}>
                <Grid container spacing={3}>
                  {!otpSent && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          className="inputOutline"
                          fullWidth
                          variant="outlined"
                          name="name"
                          label={t(`name`)}
                          value={signupData.name}
                          onChange={handleSignupChange}
                          InputLabelProps={{ shrink: true }}
                        />
                        {validator.message(
                          "name",
                          signupData.name,
                          "required"
                        )}
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          className="inputOutline"
                          fullWidth
                          variant="outlined"
                          name="email"
                          label={t(`email`)}
                          value={signupData.email}
                          onChange={handleSignupChange}
                          InputLabelProps={{ shrink: true }}
                        />
                        {authMethod === "email" &&
                          validator.message(
                            "email",
                            signupData.email,
                            "required|email"
                          )}
                      </Grid>

                      <Grid item xs={12}>
                        <TextField
                          className="inputOutline"
                          fullWidth
                          variant="outlined"
                          name="phone"
                          label={t(`phone`)}
                          value={signupData.phone}
                          onChange={handleSignupChange}
                          InputLabelProps={{ shrink: true }}
                          dir="ltr"
                        />
                        {authMethod === "phone" &&
                          validator.message(
                            "phone",
                            signupData.phone,
                            "required|phone"
                          )}
                      </Grid>

                      {authMethod === "email" && (
                        <>
                          <Grid item xs={12}>
                            <TextField
                              className="inputOutline"
                              fullWidth
                              variant="outlined"
                              name="password"
                              label={t(`password`)}
                              type="password"
                              value={signupData.password}
                              onChange={handleSignupChange}
                              InputLabelProps={{ shrink: true }}
                            />
                            {validator.message(
                              "password",
                              signupData.password,
                              "required|password"
                            )}
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              className="inputOutline"
                              fullWidth
                              variant="outlined"
                              name="confirm_password"
                              label={t(`confirm_password`)}
                              type="password"
                              value={signupData.confirm_password}
                              onChange={handleSignupChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                  )}

                  {authMethod === "phone" && otpSent && (
                    <>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label={t(`otp`)}
                          name="otp"
                          value={signupData.otp}
                          onChange={handleSignupChange}
                          variant="outlined"
                          dir="ltr"
                        />
                        {validator.message(
                          "otp",
                          signupData.otp,
                          "required|numeric"
                        )}
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          fullWidth
                          onClick={handleVerifySignupOtp}
                          disabled={loading}
                          variant="contained"
                          color="primary"
                        >
                          {t(`verifyOtp`)}
                        </Button>
                      </Grid>
                    </>
                  )}

                  {!otpSent && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        className="cBtn cBtnLarge cBtnTheme"
                        type="submit"
                        disabled={loading}
                        variant="contained"
                        color="primary"
                      >
                        {loading ? t(`loading`) : t(`register`)}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </form>
            </>
          )}

          {/* Toggle between signup and login */}
          <p className="noteHelp">
            {authTab === 0 ? (
              <>
                {t(`noAccount`)}{" "}
                <Button onClick={() => setAuthTab(1)} color="primary">
                  {t(`register`)}
                </Button>
              </>
            ) : (
              <>
                {t(`alreadyHaveAccount`)}{" "}
                <Button onClick={() => setAuthTab(0)} color="primary">
                  {t(`login`)}
                </Button>
              </>
            )}
          </p>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;